import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, Bell, BellRing, Check, CheckCheck, Clapperboard, Cog, Download, Film, RefreshCw, Sparkles, UserPlus, Users, X } from 'lucide-react';
import { api, thumbUrl } from '../lib/api';
import { ago, shouldAnimate } from '../lib/format';
import { useT, useLang } from '../lib/i18n';
import { useOverview } from '../lib/live';
import { useSession } from '../lib/session';
import { applyUpdate, useUpdate, type ReleaseNotes } from '../lib/install';
import { whatsNew } from '../lib/whatsNew';
import { joinRoom, useActiveRoom } from '../features/room/session';
import { gsap } from '../motion/gsap';
import { useLightDismiss } from '../ui/lightDismiss';
import { PushToggle } from '../features/net/PushToggle';
import { usePush } from '../lib/push';
import { canSelfUpdate, checkAppUpdate, startAppUpdate, updateAvailable, useAppUpdate } from '../lib/appUpdate';

// The bell: everything that wants your attention, in one panel. Things you can act on (an invite,
// someone waiting to join, a failed title, an update) always count towards the badge; news (a
// new film, people watching) counts until you've opened the panel once.

const SEEN_KEY = 'vb.notesSeen';
const readSeen = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]') as string[]);
  } catch {
    return new Set();
  }
};
const writeSeen = (ids: string[]) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-200)));
  } catch {
    /* private mode */
  }
};

interface Note {
  id: string;
  kind: 'invite' | 'action' | 'live' | 'work' | 'news' | 'system';
  icon: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  at?: string | number;
  thumb?: string;
  primary?: { label: string; run: () => void };
  dismiss?: () => void;
  open?: () => void;
  actionable?: boolean; // counts towards the badge until dealt with
}

type Nav = (to: string) => void;
type T = ReturnType<typeof useT>;
type Overview = NonNullable<ReturnType<typeof useOverview.getState>['data']>;

/** Room invites, newest first (not for the room this device is in). */
function inviteNotes(ov: Overview, t: T, nav: Nav, inRoom: string | null, refresh: () => void): Note[] {
  return (ov.roomInvites ?? [])
    .filter((i) => i.roomId !== inRoom)
    .map((i) => ({
      id: `invite:${i.id}`,
      kind: 'invite',
      icon: <Clapperboard size={16} />,
      title: t('notes.invite', { name: i.fromName.split(' ')[0] }),
      sub: `${i.roomName}${i.media && i.media !== i.roomName ? ` · ${i.media}` : ''}`,
      at: i.at,
      primary: {
        label: t('room.join'),
        run: () => {
          api.dismissRoomInvite(i.id).finally(refresh);
          joinRoom(i.roomId, nav);
        },
      },
      dismiss: () => void api.dismissRoomInvite(i.id).finally(refresh),
      actionable: true,
    }));
}

/** What a release adds: its headline and the first changes, the rest one tap away. */
function ReleaseSummary({ notes, lead }: { notes: ReleaseNotes; lead?: string }) {
  const t = useT();
  const [all, setAll] = useState(false);
  const shown = all ? notes.items : notes.items.slice(0, 2);
  return (
    <span className="release">
      {lead && <span className="release-lead">{lead}</span>}
      <span className="release-title">{notes.title}</span>
      <ul>
        {shown.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {notes.items.length > 2 && (
        <button type="button" className="release-more" onClick={() => setAll(!all)} aria-expanded={all}>
          {all ? t('notes.less') : t('notes.more', { n: notes.items.length - 2 })}
        </button>
      )}
    </span>
  );
}

// Read once per page load: the first load of a new release starts its "what's new" days.
const whatsNewNote = whatsNew();

/** Updates for this app, people waiting to join, titles that failed. */
function actionNotes(ov: Overview, t: T, nav: Nav, opts: { host: boolean; updateReady: boolean; updateNotes: ReleaseNotes | null; appLatest: ReturnType<typeof useAppUpdate.getState>['latest'] }): Note[] {
  const out: Note[] = [];
  const { appLatest } = opts;
  if (appLatest && updateAvailable(appLatest))
    out.push({
      id: `app:${appLatest.versionCode}`,
      kind: 'system',
      icon: <Download size={16} />,
      title: t('notes.appUpdate', { v: appLatest.version }),
      sub: t('notes.appUpdateSub'),
      primary: canSelfUpdate() ? { label: t('notes.updateNow'), run: () => void startAppUpdate(appLatest.file) } : undefined,
      open: canSelfUpdate() ? undefined : () => nav('/settings?tab=apps'),
      actionable: true,
    });
  if (opts.updateReady)
    out.push({
      id: 'update',
      kind: 'system',
      icon: <RefreshCw size={16} />,
      title: t('notes.update'),
      sub: opts.updateNotes ? <ReleaseSummary notes={opts.updateNotes} lead={t('notes.updateSub')} /> : t('notes.updateSub'),
      primary: { label: t('notes.updateNow'), run: applyUpdate },
      actionable: true,
    });
  const fresh = whatsNewNote;
  if (fresh && !opts.updateReady)
    out.push({
      id: `whatsnew:${fresh.notes.id}`,
      kind: 'system',
      icon: <Sparkles size={16} />,
      title: t('notes.whatsNew'),
      sub: <ReleaseSummary notes={fresh.notes} />,
      at: fresh.since,
    });
  if (opts.host && ov.pendingPeople)
    out.push({ id: `pending:${ov.pendingPeople}`, kind: 'action', icon: <UserPlus size={16} />, title: t('notes.pending', { n: ov.pendingPeople }), sub: t('notes.pendingSub'), open: () => nav('/team'), actionable: true });
  if (ov.pipeline.failed)
    out.push({ id: `failed:${ov.pipeline.failed}`, kind: 'action', icon: <AlertTriangle size={16} />, title: t(ov.pipeline.failed === 1 ? 'notes.failedOne' : 'notes.failed', { n: ov.pipeline.failed }), sub: t('notes.failedSub'), open: () => nav('/drive'), actionable: true });
  return out;
}

/** Rooms where people are watching right now. */
function liveNotes(ov: Overview, t: T, nav: Nav, inRoom: string | null): Note[] {
  return (ov.live ?? [])
    .filter((r) => r.id !== inRoom && r.peers.length > 0)
    .map((r) => {
      const names = r.peers.map((p) => p.name.split(' ')[0]);
      return {
        id: `live:${r.id}:${r.peers.map((p) => p.userId).sort().join(',')}`,
        kind: 'live',
        icon: <Users size={16} />,
        title: t('notes.watching', { names: names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2}` : '') }),
        sub: `${r.name}${r.media && r.media !== r.name ? ` · ${r.media}` : ''}`,
        primary: { label: t('room.join'), run: () => joinRoom(r.id, nav) },
      };
    });
}

/** Work in progress, a running network capture, and films added this week (at most 5). */
function libraryNotes(ov: Overview, t: T, nav: Nav): Note[] {
  const out: Note[] = [];
  const p = ov.pipeline;
  const working = p.uploading + p.queued + p.probing + p.remuxing + p.transcoding;
  if (working)
    out.push({ id: 'work', kind: 'work', icon: <Cog size={16} className="spin-slow" />, title: t(working === 1 ? 'notes.workingOne' : 'notes.working', { n: working }), sub: t('notes.workingSub'), open: () => nav('/drive') });
  if (ov.capture)
    out.push({ id: `capture:${ov.capture.id}`, kind: 'system', icon: <Activity size={16} />, title: t('notes.capture'), sub: ov.capture.label || t('notes.captureSub'), at: ov.capture.since, open: () => nav('/settings?tab=network') });
  const weekAgo = Date.now() - 7 * 86400_000;
  const recent = (ov.recent ?? []).filter((m) => m.playable && Date.parse(m.createdAt) >= weekAgo).slice(0, 5);
  for (const m of recent)
    out.push({ id: `film:${m.id}`, kind: 'news', icon: <Film size={16} />, title: m.title, sub: t('notes.newFilm'), at: m.createdAt, thumb: thumbUrl(m), open: () => nav('/drive') });
  return out;
}

function useNotes(): Note[] {
  const t = useT();
  const nav = useNavigate();
  const ov = useOverview((s) => s.data);
  const refresh = useOverview((s) => s.refresh);
  const host = useSession((s) => !!s.user?.host);
  const inRoom = useActiveRoom((s) => s.roomId);
  const updateReady = useUpdate((s) => s.ready);
  const updateNotes = useUpdate((s) => s.notes);
  const appLatest = useAppUpdate((s) => s.latest);
  useEffect(() => {
    checkAppUpdate();
  }, []);
  if (!ov) return [];
  return [
    ...inviteNotes(ov, t, nav, inRoom, refresh),
    ...actionNotes(ov, t, nav, { host, updateReady, updateNotes, appLatest }),
    ...liveNotes(ov, t, nav, inRoom),
    ...libraryNotes(ov, t, nav),
  ];
}

export function NotificationBell() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const notes = useNotes();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDialogElement>(null);
  const unread = notes.filter((n) => n.actionable || !seen.has(n.id)).length;
  const unseenNews = notes.some((n) => !n.actionable && !seen.has(n.id));
  const pushState = usePush((s) => s.state);

  const markSeen = () => {
    const ids = [...seen, ...notes.map((n) => n.id)];
    writeSeen(ids);
    setSeen(new Set(ids));
  };
  const close = () => {
    setOpen(false);
    markSeen();
  };

  // A native modal <dialog>: Escape (its cancel event) and a click outside (light dismiss) close
  // it, and keyboard focus stays inside while it's open.
  useEffect(() => {
    const d = panel.current;
    if (!open || !d) return;
    if (!d.open) d.showModal();
    if (shouldAnimate()) {
      gsap.fromTo(d, { y: -8, opacity: 0, scale: 0.98 }, { y: 0, opacity: 1, scale: 1, duration: 0.28, ease: 'power3.out' });
      gsap.from(d.querySelectorAll('.notif'), { y: 6, opacity: 0, duration: 0.3, stagger: 0.03, delay: 0.05, ease: 'power2.out' });
    }
  }, [open]);
  useLightDismiss(panel, open);

  // Where to drop the panel: under the bell on wide screens, a sheet on phones (CSS).
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  useEffect(() => {
    if (!open || !btn.current) return;
    const place = () => {
      const r = btn.current!.getBoundingClientRect();
      setPos({ top: r.bottom + 10, right: Math.max(12, innerWidth - r.right) });
    };
    place();
    addEventListener('resize', place);
    return () => removeEventListener('resize', place);
  }, [open]);

  const groups: { key: Note['kind'][]; label: string }[] = [
    { key: ['invite', 'action', 'system'], label: t('notes.forYou') },
    { key: ['live'], label: t('notes.liveNow') },
    { key: ['work', 'news'], label: t('notes.library') },
  ];

  return (
    <>
      <button ref={btn} className={`round-btn bell${unread ? ' has' : ''}`} aria-label={t('notes.title')} aria-expanded={open} onClick={() => (open ? close() : setOpen(true))}>
        {unread ? <BellRing size={16} /> : <Bell size={16} />}
        {unread > 0 && <span className="bell-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open &&
        createPortal(
          <dialog
            ref={panel}
            className="notes"
            aria-label={t('notes.title')}
            style={pos ? { top: pos.top, right: pos.right } : undefined}
            closedby="any"
            onCancel={(e) => {
              e.preventDefault();
              close();
            }}
          >
              <div className="notes-head">
                <b>{t('notes.title')}</b>
                {unread > 0 && <span className="notes-count">{unread}</span>}
                <span className="spacer" />
                {unseenNews && (
                  <button type="button" className="notes-read" onClick={markSeen}>
                    <CheckCheck size={14} /> {t('notes.markRead')}
                  </button>
                )}
                <button className="icon-btn" onClick={close} aria-label={t('notes.close')}>
                  <X size={16} />
                </button>
              </div>
              <div className="notes-body">
                {!notes.length && (
                  <div className="notes-empty">
                    <div className="notes-empty-icon">
                      <Check size={20} />
                    </div>
                    <b>{t('notes.allClear')}</b>
                    <span>{t('notes.allClearSub')}</span>
                  </div>
                )}
                {groups.map((g) => {
                  const list = notes.filter((n) => g.key.includes(n.kind));
                  if (!list.length) return null;
                  return (
                    <section key={g.label} className="notes-group">
                      <div className="notes-label">{g.label}</div>
                      {list.map((n) => (
                        <div key={n.id} className={`notif notif-${n.kind}${n.actionable || !seen.has(n.id) ? ' unread' : ''}${n.open ? ' clickable' : ''}`}>
                          <NoteMain
                            note={n}
                            lang={lang}
                            unread={n.actionable || !seen.has(n.id)}
                            onOpen={
                              n.open
                                ? () => {
                                    n.open!();
                                    close();
                                  }
                                : undefined
                            }
                          />
                          {(n.primary || n.dismiss) && (
                            <div className="notif-actions">
                              {n.primary && (
                                <button
                                  className="btn btn-black btn-sm"
                                  onClick={() => {
                                    n.primary!.run();
                                    close();
                                  }}
                                >
                                  {n.primary.label}
                                </button>
                              )}
                              {n.dismiss && (
                                <button className="btn btn-ghost btn-sm notif-later" onClick={n.dismiss}>
                                  {t('invite.notNow')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </section>
                  );
                })}
              </div>
              {/* Push: offered at the foot, out of the way, and gone once it's on. */}
              {pushState !== 'on' && pushState !== 'unsupported' && (
                <div className="notes-push">
                  <PushToggle compact />
                </div>
              )}
          </dialog>,
          document.body,
        )}
    </>
  );
}


/** A notification's picture and text: a button when it leads somewhere. */
function NoteMain({ note, lang, unread, onOpen }: { note: Note; lang: string; unread: boolean; onOpen?: () => void }) {
  const pic = note.thumb ? <div className="notif-thumb" style={{ backgroundImage: `url(${note.thumb})` }} /> : <div className="notif-icon">{note.icon}</div>;
  const body = (
    <>
      <div className="notif-pic">
        {pic}
        {unread && <i className="notif-dot" aria-label="New" />}
      </div>
      <div className="notif-text">
        <b className="notif-title">{note.title}</b>
        {note.sub && <div className="notif-sub">{note.sub}</div>}
        {note.at && <time className="notif-time">{ago(note.at, lang as 'en' | 'ja')}</time>}
      </div>
    </>
  );
  return onOpen ? (
    <button type="button" className="notif-main" onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="notif-main">{body}</div>
  );
}
